import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, ShieldCheck, ShieldAlert, Link2, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { UniverseLinkInput } from "@/components/UniverseLinkInput";
import { CardImage } from "@/components/CardImage";
import { useAuth } from "@/context/AuthContext";
import { useDropdowns, useAssignable } from "@/hooks/useMeta";
import { DealerSelect } from "@/components/DealerSelect";
import { PRODUCT_TYPES } from "@/lib/constants";
import { StageInfo } from "@/components/StageInfo";
import { TeamSplitPicker, splitValid } from "@/components/TeamSplitPicker";
import { UniversePrompt } from "@/components/UniversePrompt";
import { UniverseCompanyDialog } from "@/components/UniverseCompanyDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/PhoneInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ZONES = ["North", "South", "West", "Exports"];
const ADDRESS_FIELDS = [["street", "Street"], ["city", "City"], ["zip", "Zip"], ["state", "State"]];

function Field({ label, required, children, hint, info }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1">
        <Label className="text-xs font-medium text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</Label>
        {info}
      </div>
      {children}
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  );
}

function Dropdown({ value, onChange, options, placeholder, testid, allowEmpty }) {
  return (
    <Select value={value || ""} onValueChange={(v) => onChange(v === "__none" ? "" : v)}>
      <SelectTrigger className="h-9" data-testid={testid}><SelectValue placeholder={placeholder || "Select"} /></SelectTrigger>
      <SelectContent>
        {allowEmpty && <SelectItem value="__none">— None —</SelectItem>}
        {(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function SectionTitle({ children, sub }) {
  return (
    <div className="pt-1">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{children}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function autoRating(edc) {
  if (!edc) return null;
  const days = Math.round((new Date(edc) - new Date()) / 86400000);
  if (isNaN(days)) return null;
  if (days < 135) return { rating: "Hot", days };
  if (days <= 180) return { rating: "Warm", days };
  return { rating: "Cold", days };
}

export function LeadFormDialog({ open, onOpenChange, initial, onSaved }) {
  const { user } = useAuth();
  const { data: dd } = useDropdowns();
  const isGlobal = user?.role === "super_admin" || user?.role === "management";
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dups, setDups] = useState([]);
  const [confirmDup, setConfirmDup] = useState(false);
  const [reg, setReg] = useState(null);
  const [uniMatches, setUniMatches] = useState([]);
  const [uniPrompt, setUniPrompt] = useState(false);
  const [uniAdd, setUniAdd] = useState(false);
  const [promptMatches, setPromptMatches] = useState([]);

  const divisionId = isGlobal ? form.division_id : user?.division_id;
  const { data: dealers = [] } = useAssignable("dealer", divisionId);
  const isDealer = user?.role === "dealer";

  useEffect(() => {
    if (open) {
      const base = initial || { lead_status: "New", lead_source: "Self Generated", rating: "None", division_id: "univicoustic" };
      setForm({
        ...base,
        value_lakhs: base.expected_value ? base.expected_value / 10000000 : (base.value_lakhs || ""),
        edc_date: base.edc_date ? String(base.edc_date).slice(0, 10) : "",
      });
      setDups([]);
      setReg(null);
      setUniMatches([]);
      setUniPrompt(false);
      setUniAdd(false);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // duplicate detection (debounced) — CRM-wide, matches phone/email/company/project name
  useEffect(() => {
    if (!open || initial?.id) return;
    const t = setTimeout(async () => {
      if (!form.phone && !form.email && !form.company && !form.project_name) return;
      try {
        const { data } = await api.get("/leads/check-duplicate", {
          params: { phone: form.phone || "", email: form.email || "", company: form.company || "", project: form.project_name || "" },
        });
        setDups(data.duplicates || []);
        setConfirmDup(false);
      } catch (e) { console.warn("Duplicate check failed:", e); }
    }, 600);
    return () => clearTimeout(t);
  }, [form.phone, form.email, form.company, form.project_name, open, initial]);

  // deal-registration check (debounced)
  useEffect(() => {
    if (!open || initial?.id) return;
    const t = setTimeout(async () => {
      if (!form.phone && !form.company) { setReg(null); return; }
      try {
        const { data } = await api.get("/leads/check-registration", {
          params: { phone: form.phone || "", company: form.company || "" },
        });
        setReg(data.registered ? data : null);
      } catch { setReg(null); }
    }, 600);
    return () => clearTimeout(t);
  }, [form.phone, form.company, open, initial]);

  // Universe auto-match suggestions (debounced)
  useEffect(() => {
    if (!open || isDealer || form.universe_id) { setUniMatches([]); return; }
    const t = setTimeout(async () => {
      if (!(form.company || "").trim()) { setUniMatches([]); return; }
      try {
        const { data } = await api.get("/master-data/match", { params: { q: form.company } });
        setUniMatches(data || []);
      } catch { setUniMatches([]); }
    }, 500);
    return () => clearTimeout(t);
  }, [form.company, form.universe_id, open, isDealer]);

  const showLostReason = ["Lead Lost"].includes(form.lead_status);
  const auto = form.rating && form.rating !== "None" ? null : autoRating(form.edc_date);

  const submit = async (overrides = {}) => {
    const universeId = overrides.universe_id || form.universe_id;
    const universeCompany = overrides.universe_company || form.universe_company;
    const companyVal = (form.company || "").trim() || (universeId ? (universeCompany || "").trim() : "");
    if (isDealer) {
      if (!form.name || !form.company || !form.phone || !form.location) {
        toast.error("Name, Company, Phone/Mobile and Location are required");
        return;
      }
    } else {
      const missing = [];
      if (!companyVal) missing.push("Universe Company");
      if (!form.project_name?.trim()) missing.push("Project Name");
      if (!form.zone) missing.push("Zone");
      if (!form.branch?.trim()) missing.push("Branch");
      if (!["Yes", "No"].includes(form.univic_specified)) missing.push("UniVicoustic Specified");
      if (!(Number(form.value_lakhs) > 0)) missing.push("Value (INR Cr)");
      if (!form.edc_date) missing.push("EDC");
      if (missing.length) { toast.error(`Required: ${missing.join(", ")}`); return; }
    }
    if (!splitValid(form.team_split)) { toast.error("Team split must total 100%"); return; }
    if (isGlobal && !form.division_id && !initial?.id) { toast.error("Select a division"); return; }
    if (!initial?.id && dups.length > 0 && !confirmDup) {
      setConfirmDup(true);
      document.querySelector('[data-testid="duplicate-warning"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast.warning("Possible duplicate — check the matched leads shown at the top of the form, then press “Create Anyway” if this is a different project.");
      return;
    }
    // every lead must be linked to a Universe company
    if (!isDealer && !initial?.id && !universeId) {
      let ms = [];
      try {
        ({ data: ms } = await api.get("/master-data/match", { params: { q: companyVal } }));
      } catch { ms = []; }
      const exact = (ms || []).find((m) => (m.company || "").trim().toLowerCase() === companyVal.toLowerCase());
      if (exact) {
        setForm((x) => ({ ...x, universe_id: exact.id, universe_company: exact.company }));
        return submit({ universe_id: exact.id, universe_company: exact.company });
      }
      setPromptMatches(ms || []);
      setUniPrompt(true);
      return;
    }
    setSaving(true);
    try {
      const { value_lakhs, ...payload } = form;
      if (!isDealer) payload.expected_value = Number(value_lakhs || 0) * 10000000;
      if (!(payload.company || "").trim() && companyVal) payload.company = companyVal;
      if (overrides.universe_id) { payload.universe_id = overrides.universe_id; payload.universe_company = overrides.universe_company; }
      let saved = null;
      if (initial?.id) {
        saved = (await api.put(`/leads/${initial.id}`, payload)).data;
        toast.success("Lead updated");
      } else {
        saved = (await api.post("/leads", payload)).data;
        toast.success("Lead created");
      }
      onOpenChange(false);
      onSaved?.(saved);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{initial?.id ? "Edit Lead" : "New Lead"}</DialogTitle>
        </DialogHeader>

        {dups.length > 0 && (
          <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2.5 flex gap-2 text-sm text-red-800" data-testid="duplicate-warning">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold">Possible duplicate — {dups.length} existing lead{dups.length > 1 ? "s" : ""} match{dups.length > 1 ? "" : "es"} this name</p>
              <ul className="text-xs mt-1 space-y-0.5">
                {dups.slice(0, 5).map((d) => (
                  <li key={d.id} className="truncate">
                    <span className="font-medium">{d.project_name || d.company}</span>
                    {d.company && d.project_name ? ` · ${d.company}` : ""}
                    {d.owner_name ? ` — owned by ${d.owner_name}` : ""}
                    {d.lead_status ? ` (${d.lead_status})` : ""}
                  </li>
                ))}
              </ul>
              <p className="text-xs mt-1.5">Please check with the owner before creating a duplicate. If this is genuinely a different project, you can still create it.</p>
            </div>
          </div>
        )}

        {reg && (
          <div className={`rounded-md border px-3 py-2 flex gap-2 text-sm ${reg.mine ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-800"}`} data-testid="registration-warning">
            {reg.mine ? <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0" /> : <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />}
            <div>
              <p className="font-medium">
                {reg.mine ? "You have registered this enquiry" : `Deal registered by ${reg.dealer_name}`}
              </p>
              <p className="text-xs">
                {reg.mine
                  ? `Active until ${(reg.expires_at || "").slice(0, 10)}.`
                  : `Protected until ${(reg.expires_at || "").slice(0, 10)}.${isDealer ? " You cannot log this enquiry." : ""}`}
              </p>
            </div>
          </div>
        )}

        {!isDealer && <SectionTitle sub={`Sales member: ${user?.name || ""}`}>Core details (required)</SectionTitle>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {isGlobal && !initial?.id && (
            <Field label="Division" required>
              <Dropdown value={form.division_id} onChange={(v) => set("division_id", v)}
                options={["univicoustic"]} testid="lead-division" />
            </Field>
          )}
          <Field label="Project Name" required={!isDealer}>
            <Input value={form.project_name || ""} onChange={(e) => set("project_name", e.target.value)} className="h-9" data-testid="lead-project_name" />
          </Field>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">{isDealer ? "Company" : "Universe Company"}<span className="text-red-500"> *</span></Label>
            <Input value={form.company || ""} onChange={(e) => set("company", e.target.value)} placeholder="The Universe company this lead belongs to" className="h-9" data-testid="lead-company" />
            {form.universe_id ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded" data-testid="universe-linked">
                <Link2 className="h-3 w-3" /> Universe: {form.universe_company}
                <button onClick={() => setForm((f) => ({ ...f, universe_id: null, universe_company: "" }))} className="ml-0.5 hover:text-emerald-900" data-testid="universe-unlink"><X className="h-3 w-3" /></button>
              </span>
            ) : uniMatches.length > 0 && (
              <div className="flex flex-wrap gap-1" data-testid="universe-suggestions">
                {uniMatches.slice(0, 3).map((m) => (
                  <button key={m.id} onClick={() => setForm((f) => ({ ...f, universe_id: m.id, universe_company: m.company }))}
                    className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100 hover:bg-slate-200 border border-slate-200 px-1.5 py-0.5 rounded transition-colors"
                    data-testid={`universe-suggest-${m.id}`}>
                    <Link2 className="h-3 w-3" /> Link to {m.company}{m.location ? ` (${m.location})` : ""}
                  </button>
                ))}
              </div>
            )}
          </div>
          {!isDealer && (
            <>
              <Field label="Zone" required>
                <Dropdown value={form.zone} onChange={(v) => set("zone", v)} options={ZONES} testid="lead-zone" />
              </Field>
              <Field label="Branch" required>
                <Input value={form.branch || ""} onChange={(e) => set("branch", e.target.value)} placeholder="e.g. Mumbai, Delhi, Bangalore" className="h-9" data-testid="lead-branch" />
              </Field>
              <Field label="UniVicoustic Specified" required>
                <Dropdown value={form.univic_specified} onChange={(v) => set("univic_specified", v)} options={["Yes", "No"]} testid="lead-univic_specified" />
              </Field>
              <Field label="Value (INR Cr)" required>
                <Input type="number" value={form.value_lakhs ?? ""} onChange={(e) => set("value_lakhs", e.target.value)} className="h-9" data-testid="lead-value_lakhs" />
              </Field>
              <Field label="Expected Date of Closure (EDC)" required
                hint={auto ? `Auto rating: ${auto.rating} — ${auto.days >= 0 ? `closes in ${auto.days} days` : "past due"} (Hot <135d · Warm 135–180d · Cold >180d)` : "Hot <135d · Warm 135–180d · Cold >180d"}>
                <Input type="date" value={form.edc_date || ""} onChange={(e) => set("edc_date", e.target.value)} className="h-9" data-testid="lead-edc_date" />
              </Field>
              <Field label="Rating" hint="Leave 'None' to auto-rate from EDC; picking one overrides it">
                <Dropdown value={form.rating} onChange={(v) => set("rating", v)} options={dd?.rating} testid="lead-rating" />
              </Field>
            </>
          )}
        </div>

        {!isDealer && (
          <>
            <SectionTitle sub="Must be filled before the lead can be Qualified / converted">Additional required info</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Dealer Involved">
                <DealerSelect zone={form.zone} dealerInvolved={form.dealer_involved} dealerRecordId={form.dealer_record_id}
                  onChange={(v) => setForm((f) => ({ ...f, ...v }))} testid="lead-dealer-involved" />
              </Field>
              <Field label="Identified (date / stage)">
                <Input value={form.identified || ""} onChange={(e) => set("identified", e.target.value)} placeholder="e.g. Design stage, Apr 2026" className="h-9" data-testid="lead-identified" />
              </Field>
              <Field label="Architect / Designer / Lead Designer">
                <UniverseLinkInput value={form.architect} universeId={form.architect_universe_id} universeCompany={form.architect_universe_company}
                  onChange={(v) => setForm((f) => ({ ...f, architect: v.value, architect_universe_id: v.universe_id, architect_universe_company: v.universe_company }))}
                  placeholder="Type to search the Universe" testid="lead-architect" />
              </Field>
              <Field label="D&B Firm / General Contractor">
                <UniverseLinkInput value={form.dnb_gc} universeId={form.dnb_gc_universe_id} universeCompany={form.dnb_gc_universe_company}
                  onChange={(v) => setForm((f) => ({ ...f, dnb_gc: v.value, dnb_gc_universe_id: v.universe_id, dnb_gc_universe_company: v.universe_company }))}
                  placeholder="Type to search the Universe" testid="lead-dnb_gc" />
              </Field>
              <Field label="End Client — Decision Maker">
                <Input value={form.end_client_name || ""} onChange={(e) => set("end_client_name", e.target.value)} className="h-9" data-testid="lead-end_client_name" />
              </Field>
              <Field label="Designation">
                <Input value={form.designation || ""} onChange={(e) => set("designation", e.target.value)} className="h-9" data-testid="lead-designation" />
              </Field>
              <Field label="End Client — Contact Number">
                <PhoneInput value={form.end_client_phone || ""} onChange={(v) => set("end_client_phone", v)} testid="lead-end_client_phone" />
              </Field>
              <Field label="End Client — Email">
                <Input value={form.end_client_email || ""} onChange={(e) => set("end_client_email", e.target.value)} className="h-9" data-testid="lead-end_client_email" />
              </Field>
              <Field label="Competition">
                <Input value={form.competition || ""} onChange={(e) => set("competition", e.target.value)} placeholder="Competing brands / vendors" className="h-9" data-testid="lead-competition" />
              </Field>
              <Field label="Type of Product">
                <Dropdown value={form.product_type} onChange={(v) => set("product_type", v)} options={dd?.product_type || PRODUCT_TYPES} allowEmpty testid="lead-product_type" />
              </Field>
              <Field label="Total Area (Lakh sqft)">
                <Input type="number" value={form.area_lacs_sqft || ""} onChange={(e) => set("area_lacs_sqft", e.target.value)} className="h-9" data-testid="lead-area_lacs_sqft" />
              </Field>
            </div>
          </>
        )}

        <SectionTitle>{isDealer ? "Contact & other details" : "Other details"}</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {isDealer && (
            <>
              <Field label="Contact Name" required={isDealer}>
                <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="h-9" data-testid="lead-name" />
              </Field>
              <Field label="Phone / Mobile" required={isDealer}>
                <PhoneInput value={form.phone || ""} onChange={(v) => set("phone", v)} testid="lead-phone" />
              </Field>
              <Field label="Email">
                <Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="h-9" data-testid="lead-email" />
              </Field>
              <Field label="Designation">
                <Input value={form.designation || ""} onChange={(e) => set("designation", e.target.value)} className="h-9" data-testid="lead-designation" />
              </Field>
            </>
          )}
          <Field label="Location" required={isDealer}>
            <Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} className="h-9" data-testid="lead-location" />
          </Field>
          <Field label="Account Category"><Dropdown value={form.account_category} onChange={(v) => set("account_category", v)} options={dd?.account_category} allowEmpty testid="lead-account_category" /></Field>
          <Field label="Industry"><Dropdown value={form.industry} onChange={(v) => set("industry", v)} options={dd?.industry} allowEmpty testid="lead-industry" /></Field>
          <Field label="Inquiry Type"><Dropdown value={form.inquiry_type} onChange={(v) => set("inquiry_type", v)} options={dd?.inquiry_type} allowEmpty testid="lead-inquiry_type" /></Field>
          <Field label="Lead Status" required info={<StageInfo kind="lead" />}><Dropdown value={form.lead_status} onChange={(v) => set("lead_status", v)} options={dd?.lead_status} testid="lead-lead_status" /></Field>
          <Field label="Lead Source" required><Dropdown value={form.lead_source} onChange={(v) => set("lead_source", v)} options={dd?.lead_source} testid="lead-lead_source" /></Field>
          <Field label="Event / Exhibition" hint="Where you met this contact (optional)"><Input value={form.event_name || ""} onChange={(e) => set("event_name", e.target.value)} placeholder="e.g. ACETECH Mumbai 2026" className="h-9" data-testid="lead-event_name" /></Field>
          {form.card_scan_id && <div className="sm:col-span-2"><CardImage scanId={form.card_scan_id} /></div>}
          {!isDealer && (
            <div className="sm:col-span-2">
              <Field label="Team members involved" hint="Optional — select 2+ members and set how the sales value splits between them (must total 100%).">
                <TeamSplitPicker value={form.team_split || []} onChange={(v) => set("team_split", v)} testid="lead-team-split-picker" />
              </Field>
            </div>
          )}
          {showLostReason && (
            <Field label="Lost Reason">
              <Dropdown value={form.lost_reason} onChange={(v) => set("lost_reason", v)} options={dd?.lost_reason} allowEmpty testid="lead-lost_reason" />
            </Field>
          )}
          {!isDealer && (
            <Field label="Dealer Portal Account (optional)">
              <Select value={form.dealer_assigned_id || ""} onValueChange={(v) => set("dealer_assigned_id", v === "__none" ? null : v)}>
                <SelectTrigger className="h-9" data-testid="lead-dealer"><SelectValue placeholder="Select dealer login" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— None —</SelectItem>
                  {dealers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="No. of Employees"><Input type="number" value={form.no_of_employees || ""} onChange={(e) => set("no_of_employees", e.target.value)} className="h-9" data-testid="lead-employees" /></Field>
          <Field label="Annual Revenue (INR)"><Input type="number" value={form.annual_revenue || ""} onChange={(e) => set("annual_revenue", e.target.value)} className="h-9" data-testid="lead-revenue" /></Field>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {ADDRESS_FIELDS.map(([k, label]) => (
            <Field key={k} label={label}><Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="h-9" data-testid={`lead-${k}`} /></Field>
          ))}
        </div>

        {!initial?.id && !isDealer && (
          <label className="flex items-center gap-2.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 cursor-pointer" data-testid="auto-assign-row">
            <Checkbox checked={!!form.auto_assign} onCheckedChange={(v) => set("auto_assign", !!v)} data-testid="lead-auto-assign" />
            <div>
              <p className="text-sm font-medium text-slate-800">Auto-assign (round-robin)</p>
              <p className="text-xs text-slate-500">Distribute this enquiry to the next sales rep in rotation instead of yourself.</p>
            </div>
          </label>
        )}

        <Field label="Description"><Textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="lead-description" /></Field>
        <Field label="Remarks"><Textarea value={form.remarks || ""} onChange={(e) => set("remarks", e.target.value)} rows={2} data-testid="lead-remarks" /></Field>

        <UniversePrompt open={uniPrompt} onOpenChange={setUniPrompt} company={(form.company || "").trim()} matches={promptMatches}
          entityLabel="lead"
          onPick={(m) => { setUniPrompt(false); setForm((x) => ({ ...x, universe_id: m.id, universe_company: m.company })); submit({ universe_id: m.id, universe_company: m.company }); }}
          onAdd={() => { setUniPrompt(false); setUniAdd(true); }} />
        <UniverseCompanyDialog open={uniAdd} onOpenChange={setUniAdd} initial={{ company: (form.company || "").trim() }} skipDupGate
          onSaved={(doc) => { setForm((x) => ({ ...x, universe_id: doc.id, universe_company: doc.company })); submit({ universe_id: doc.id, universe_company: doc.company }); }} />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="lead-cancel">Cancel</Button>
          <Button onClick={() => submit()} disabled={saving} className={`gap-2 ${!initial?.id && dups.length > 0 && confirmDup ? "bg-red-600 hover:bg-red-700" : "bg-slate-900 hover:bg-slate-800"}`} data-testid="lead-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {initial?.id ? "Save Changes" : dups.length > 0 && confirmDup ? "Create Anyway" : "Create Lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
